import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./client";
import type {
  AgentRef, Attachment, Category, CategoryBreakdownRow, Comment, DirectoryUser,
  LifecycleReport, Me, NotificationRow, Queue, Role, Ticket, TicketTimeline,
} from "../types/models";

export const useMe = () => useQuery({ queryKey: ["me"], queryFn: () => api.get<{ user: Me }>("/api/auth/me") });

export const useCategories = () =>
  useQuery({ queryKey: ["categories"], queryFn: () => api.get<Category[]>("/api/categories") });

export const useQueues = () =>
  useQuery({ queryKey: ["queues"], queryFn: () => api.get<Queue[]>("/api/queues") });

export interface TicketsPage { items: Ticket[]; total: number; page: number; pageSize: number; }
export const useTickets = (params: Record<string, string | number | undefined> = {}) => {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== "") qs.set(k, String(v));
  return useQuery({
    queryKey: ["tickets", qs.toString()],
    queryFn: () => api.get<TicketsPage>(`/api/tickets?${qs.toString()}`),
  });
};

export const useTicket = (id: string | undefined) =>
  useQuery({
    queryKey: ["ticket", id],
    queryFn: () => api.get<Ticket>(`/api/tickets/${id}`),
    enabled: !!id,
  });

export const useComments = (id: string | undefined) =>
  useQuery({
    queryKey: ["ticket", id, "comments"],
    queryFn: () => api.get<Comment[]>(`/api/tickets/${id}/comments`),
    enabled: !!id,
  });

export const useAttachments = (id: string | undefined) =>
  useQuery({
    queryKey: ["ticket", id, "attachments"],
    queryFn: () => api.get<Attachment[]>(`/api/tickets/${id}/attachments`),
    enabled: !!id,
  });

export const useNotifications = () =>
  useQuery({ queryKey: ["notifications"], queryFn: () => api.get<NotificationRow[]>("/api/notifications") });

export const useCreateTicket = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<Ticket>) => api.post<Ticket>("/api/tickets", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tickets"] }),
  });
};

export const useAddComment = (ticketId: string | undefined, internal = false) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: string) =>
      api.post<Comment>(`/api/tickets/${ticketId}/comments${internal ? "/internal" : ""}`, { body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ticket", ticketId, "comments"] }),
  });
};

export const useChangeStatus = (ticketId: string | undefined) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { status: string; note?: string }) =>
      api.post<Ticket>(`/api/tickets/${ticketId}/status`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ticket", ticketId] });
      qc.invalidateQueries({ queryKey: ["tickets"] });
      qc.invalidateQueries({ queryKey: ["search"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
};

export const useUpdateTicket = (ticketId: string | undefined) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<Pick<Ticket, "title" | "description" | "priority" | "categoryId" | "department" | "location" | "deviceDetails">>) =>
      api.patch<Ticket>(`/api/tickets/${ticketId}`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ticket", ticketId] });
      qc.invalidateQueries({ queryKey: ["tickets"] });
      qc.invalidateQueries({ queryKey: ["search"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
};

export const useAssign = (ticketId: string | undefined) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { assigneeId?: string; queueId?: string }) =>
      api.post<Ticket>(`/api/tickets/${ticketId}/assign`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ticket", ticketId] }),
  });
};

export const useUploadAttachment = (ticketId: string | undefined) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append("file", file);
      return api.post<Attachment>(`/api/tickets/${ticketId}/attachments`, fd);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ticket", ticketId, "attachments"] }),
  });
};

export const useRate = (ticketId: string | undefined) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { score: number; comment?: string }) =>
      api.post(`/api/tickets/${ticketId}/rate`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ticket", ticketId] }),
  });
};

// Users / directory

export const useAgents = () =>
  useQuery({ queryKey: ["users", "agents"], queryFn: () => api.get<AgentRef[]>("/api/users/agents") });

export const useDirectorySearch = (q: string, enabled = true) =>
  useQuery({
    queryKey: ["users", "directory", q],
    queryFn: () => api.get<DirectoryUser[]>(`/api/users/directory${q ? `?q=${encodeURIComponent(q)}` : ""}`),
    enabled: enabled && q.trim().length >= 1,
  });

export const useImportUser = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { aadObjectId: string; role: Role }) => api.post("/api/users/import", input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
    },
  });
};

// Reports

interface DateRange { from: string; to: string }

export const useLifecycleReport = (range: DateRange) =>
  useQuery({
    queryKey: ["report", "lifecycle", range.from, range.to],
    queryFn: () => api.get<LifecycleReport>(`/api/reports/lifecycle?from=${range.from}&to=${range.to}`),
  });

export const useCategoryBreakdown = (range: DateRange) =>
  useQuery({
    queryKey: ["report", "category-breakdown", range.from, range.to],
    queryFn: () => api.get<CategoryBreakdownRow[]>(`/api/reports/category-breakdown?from=${range.from}&to=${range.to}`),
  });

export const useTicketTimeline = (idOrNumber: string | undefined) =>
  useQuery({
    queryKey: ["report", "timeline", idOrNumber],
    queryFn: () => api.get<TicketTimeline>(`/api/reports/ticket/${idOrNumber}/timeline`),
    enabled: !!idOrNumber,
    retry: false,
  });
